import api, { apiHelpers } from './api';

/**
 * Authentication Service
 * Handles user authentication, registration, and profile management
 */
const authService = {
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} - Registration response with user and token
   */
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      
      // Store token and user data in localStorage
      if (response.data.token) {
        apiHelpers.setAuthToken(response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error) {
      throw error.message || 'Registration failed';
    }
  },

  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} - Login response with user and token
   */
  login: async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });

      // Store token and user data in localStorage
      if (response.data.token) {
        apiHelpers.setAuthToken(response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      return response.data;
    } catch (error) {
      // The axios response interceptor in `services/api.js` has already
      // flattened the response into a real Error whose `.message` is the
      // backend's `detail` (or `message`) field. Propagate it as-is so
      // deactivation / lockout reasons reach the login screen instead of
      // being squashed into "Login failed".
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(typeof error === 'string' ? error : 'Login failed');
    }
  },

  /**
   * Logout user
   * @returns {Promise<void>}
   */
  logout: async () => {
    try {
      // Call logout API endpoint if available
      await api.get('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear token and user data regardless of API response
      apiHelpers.clearAuth();
    }
  },

  /**
   * Get current user and validate token
   * @returns {Promise<Object>} - Current user data
   */
  validateToken: async () => {
    try {
      const response = await api.get('/auth/me');
      
      // Update stored user data with fresh data
      if (response.data.data) {
        localStorage.setItem('user', JSON.stringify(response.data.data));
      }
      
      return response.data.data;
    } catch (error) {
      // Clear invalid token
      apiHelpers.clearAuth();
      throw error.message || 'Authentication failed';
    }
  },

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<Object>} - Password reset response
   */
  forgotPassword: async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw error.message || 'Failed to process password reset';
    }
  },

  /**
   * Reset password
   * @param {string} token - Reset token
   * @param {string} password - New password
   * @returns {Promise<Object>} - Password reset confirmation
   */
  resetPassword: async (token, password) => {
    try {
      const response = await api.post(`/auth/reset-password/${token}`, { password });
      return response.data;
    } catch (error) {
      throw error.message || 'Failed to reset password';
    }
  },

  /**
   * Update user profile
   * @param {Object} userData - User data to update
   * @returns {Promise<Object>} - Updated user profile
   */
  updateProfile: async (userData) => {
    try {
      // Get current user ID from localStorage
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = currentUser._id;
      
      if (!userId) {
        throw new Error('User ID not found');
      }
      
      console.log('authService.updateProfile - Sending:', userData);
      console.log('authService.updateProfile - User ID:', userId);
      
      // Use the correct endpoint with user ID
      const response = await api.put(`/users/${userId}`, userData);
      console.log('authService.updateProfile - Response:', response);
      console.log('authService.updateProfile - Response data:', response.data);
      
      // Update stored user data
      if (response.data && (response.data.data || response.data.user)) {
        const updatedUserData = response.data.data || response.data.user || response.data;
        console.log('authService.updateProfile - Updated user data:', updatedUserData);
        
        const updatedUser = {
          ...currentUser,
          ...updatedUserData
        };
        console.log('authService.updateProfile - Merged user:', updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Return the fully merged user object
        return updatedUser;
      }
      
      return response.data;
    } catch (error) {
      console.error('authService.updateProfile - Error:', error);
      throw error.response?.data?.message || error.message || 'Failed to update profile';
    }
  },
  
  /**
   * Upload profile image
   * @param {File} imageFile - Image file to upload
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<Object>} - Updated user profile with image URL
   */
  uploadProfileImage: async (imageFile, onProgress = null) => {
    try {
      const formData = new FormData();
      
      formData.append('profileImage', imageFile);
     
      // Use the uploadFile helper for better handling
      const response = await apiHelpers.uploadFile(
        '/users/profile/image', 
        formData,
        onProgress
      );
      
      // Log the response for debugging
      console.log('Profile image upload response:', response);
      console.log('Response data:', response.data);
      
      // The backend returns {success: true, data: {profileImage: "...", user: {...}}}
      if (response.data && response.data.success && response.data.data) {
        const responseData = response.data.data;
        console.log('Extracted response data:', responseData);
        
        if (responseData.profileImage) {
          const profileImageUrl = responseData.profileImage;
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          const updatedUser = {
            ...currentUser,
            profileImage: profileImageUrl
          };
          console.log('Updated user object:', updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
          
          // Return the updated user data in the expected format
          return {
            ...updatedUser,
            profileImage: profileImageUrl
          };
        }
      }
      
      console.error('Unexpected response structure:', response.data);
      throw new Error('Failed to get profile image URL from response');
    } catch (error) {
      throw error.message || 'Failed to upload profile image';
    }
  },

  /**
   * Delete profile image
   * @returns {Promise<Object>} - Updated user profile without image
   */
  deleteProfileImage: async () => {
    try {
      const response = await api.delete('/users/profile/image');

      console.log('Profile image delete response:', response);

      if (response.data && response.data.success) {
        // Update localStorage to remove profileImage
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = {
          ...currentUser,
          profileImage: null
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));

        return updatedUser;
      }

      throw new Error('Failed to delete profile image');
    } catch (error) {
      throw error.message || 'Failed to delete profile image';
    }
  },

  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - Password change confirmation
   */
  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      throw error.message || 'Failed to change password';
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} - Authentication status
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  /**
   * Check if user has admin role
   * @returns {boolean} - Admin status
   */
  isAdmin: () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user && user.role === 'admin';
    } catch (error) {
      return false;
    }
  },

  /**
   * Check if user has specific permission
   * @param {string} resource - Resource to check (e.g., 'projects', 'documents')
   * @param {string} action - Action to check (e.g., 'create', 'update')
   * @returns {boolean} - Permission status
   */
  hasPermission: (resource, action) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Admin always has all permissions
      if (user.role === 'admin') {
        return true;
      }
      
      // Check specific permission
      if (user.permissions) {
        const permission = user.permissions.find(p => p.resource === resource);
        return permission && permission.actions.includes(action);
      }
      
      return false;
    } catch (error) {
      return false;
    }
  },

  /**
   * Get stored user data
   * @returns {Object|null} - User data or null if not logged in
   */
  getCurrentUser: () => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch (error) {
      return null;
    }
  }
};

export default authService;